<template>
  <Card>
    <template #header>
      <View justify="space-between" class="stats-title" align="center">
        <Text variant="h2">Statistics</Text>

        <MenuButton :buttonLabel="selectedFilter" size="medium">
          <MenuButtonItem
            v-for="filter in timeFilters"
            :key="filter"
            :title="filter"
            @click="onFilterSelected(filter)"
          />
        </MenuButton>
      </View>
    </template>

    <View gap="md" class="property-statistics">
      <Card :as="RouterLink" :to="propertyLeadsRoute" size="medium" variant="tertiary" class="leads-row">
        <View gap="sm" direction="column">
          <Text variant="h5">Total leads</Text>
          <View justify="space-between">
            <View direction="column" gap="xs">
              <Text variant="h3">{{ statistics?.leads || 0 }}</Text>
              <Text variant="label-small" color="tertiary" class="label">Leads</Text>
            </View>
          </View>
        </View>
      </Card>

      <Card size="medium" variant="outline" v-for="stat in statCards" :key="stat.title">
        <View gap="sm" direction="column">
          <Text variant="h5">{{ stat.title }}</Text>
          <View gap="md" justify="space-between">
            <View v-for="item in stat.items" :key="item.label" direction="column" gap="xs">
              <Text variant="h3">{{ item.count }}</Text>
              <Text variant="label-small" color="tertiary" class="label">
                {{ item.label }}
              </Text>
            </View>
          </View>
        </View>
      </Card>
    </View>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouteLocationRaw, RouterLink } from 'vue-router';

import { Card, MenuButton, MenuButtonItem, Text, View } from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';
import { usePropertyStatisticsQuery } from '@tallo/investor/property';
import { TimeFilter, timeFilters } from '@tallo/utility';

const props = defineProps<{ propertyId: string }>();

const { data, selectedFilter } = usePropertyStatisticsQuery();
const statistics = computed(() => data.value);

function onFilterSelected(filter: TimeFilter) {
  selectedFilter.value = filter;
}

const statCards = computed(() => [
  {
    title: 'Conversation insights',
    items: [
      {
        label: 'Messages sent',
        count: statistics.value?.messagesSent || 0,
      },
      {
        label: 'Follow-ups sent',
        count: statistics.value?.followUpsSent || 0,
      },
    ],
  },
  {
    title: 'Showing insights',
    items: [
      {
        label: 'Total showings',
        count: statistics.value?.showingsTotal || 0,
      },
      {
        label: 'Booked showings',
        count: statistics.value?.showingsCompleted || 0,
      },
    ],
  },
]);

const propertyLeadsRoute = computed<RouteLocationRaw>(() => ({
  name: AppRouteNames.INVESTOR_PROPERTY_LEADS,
  params: {
    propertyId: props.propertyId,
  },
  query: {
    filter: 'All',
  },
}));
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.property-statistics {
  @include screen-max-md {
    flex-direction: column;
  }
}

.leads-row {
  width: 100%;
  padding: var(--t-spacing-sm) 2.5rem;
  max-width: 12rem;

  @include screen-max-md {
    max-width: none;
    padding: var(--t-spacing-sm) 0;
  }
}

.stats-title {
  width: 100%;
}

.label {
  text-wrap: nowrap;
}
</style>
